import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IDevice } from "@formant/core/model/IDevice";
import { IValidation } from "@formant/core/model/IValidation";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import { track } from "../../analytics";
import { deviceStore, observeStore } from "../../stores";
import { ButtonsBar } from "../common/ButtonsBar";
import { Popup } from "../common/Popup";
import { StandardButton } from "../common/StandardButton";
import { ValidationMessages } from "../common/ValidationMessages";

export interface IDeviceConfigurationPopupProps {
    device: IDevice;
    onClose: () => void;
}

@observer
export class DeviceConfigurationPopup extends Component<IDeviceConfigurationPopupProps> {
    @observable
    private name = this.props.device.name;

    @observable
    validation: IValidation = {};

    @observable
    loading = false;

    render() {
        const { device } = this.props;
        const { validation, loading } = this;
        return (
            <Popup title="Rename" visible={observeStore.editingDevice}>
                <TextField
                    label="Name"
                    small={true}
                    value={this.name}
                    onChange={this.onChangeName}
                    invalid={!!validation.name}
                />
                <ValidationMessages validation={validation} keys={["name"]} />
                <ButtonsBar minimal={true}>
                    <StandardButton onClicked={this.onHandleClose}>
                        Cancel
                    </StandardButton>
                    <StandardButton
                        primary={true}
                        onClicked={this.onSave}
                        loading={loading}
                        disabled={this.name === device.name}
                    >
                        Save
                    </StandardButton>
                </ButtonsBar>
            </Popup>
        );
    }

    onHandleClose = () => {
        this.validation = {};
        this.props.onClose();
    };

    private onSave = async () => {
        const { device } = this.props;
        try {
            this.loading = true;
            await deviceStore.update(defined(device.id), {
                name: this.name,
            });
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            const { statusCode } = responseError;
            if (statusCode === 422) {
                track("Device Configuration: Validation Error On Device Save");
                this.validation = responseError.validationErrors;
            } else if (statusCode === 409) {
                this.validation = {
                    name: ["A device with this name already exists"],
                };
            }
            return;
        } finally {
            this.loading = false;
        }
        this.onHandleClose();
    };

    private onChangeName = (event: ChangeEvent<InputElement>) => {
        const name = event.target.value;
        this.name = name;
    };
}
